import { Box, Button, Skeleton, styled, Typography } from '@mui/material';
import theme from 'src/theme';

export const RootBox = styled(Box)(({ theme: muiTheme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: muiTheme.spacing(10),
    marginBottom: muiTheme.spacing(10),
    marginLeft: muiTheme.spacing(2),
    marginRight: muiTheme.spacing(2),
    [muiTheme.breakpoints.up('md')]: {
        marginLeft: muiTheme.spacing(40),
        marginRight: muiTheme.spacing(40)
    }
}));

export const PageTitle = styled(Typography)`
    font-size: 24px;
    font-weight: 600;
    color: ${theme.color.white};
    margin-bottom: 24px;
`;

export const InfoCard = styled(Box)`
    background-color: ${theme.color.blackRock};
    border-radius: 4px;
    padding: 24px;
    margin-bottom: 24px;
`;

export const InfoRow = styled(Box)`
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    &:last-child {
        margin-bottom: 0;
    }
`;

export const InfoRowTopAligned = styled(InfoRow)`
    align-items: flex-start;
`;

export const InfoLabel = styled(Typography)`
    font-size: 13px;
    color: ${theme.color.white50};
    min-width: 120px;
`;

export const InfoValue = styled(Typography)`
    font-size: 13px;
    color: ${theme.color.white};
`;

export const InfoValueMultiline = styled(InfoValue)`
    white-space: pre-wrap;
    line-height: 1.6;
`;

export const DescriptionMarkdownContainer = styled(Box)`
    flex: 1;
    overflow: hidden;
    .wmde-markdown {
        background-color: transparent;
        font-size: 13px;
        color: ${theme.color.white};

        a {
            text-decoration: underline;
            &:hover {
                text-decoration: none;
            }
        }

        pre {
            padding: 1em;
            border-radius: 4px;
            overflow-x: auto;
        }

        p:first-of-type {
            margin-top: 0;
        }

        p:last-of-type {
            margin-bottom: 0;
        }
    }
`;

export const InfoLink = styled(InfoValue)`
    color: ${theme.color.lightningYellow};
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

export const StatusBadge = styled(Box)<{
    statusColor?: string;
    statusTextColor?: string;
}>`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => props.statusTextColor || theme.color.white};
    background-color: ${(props) =>
        props.statusColor || theme.color.darkMintYellow};
`;

export const HistoryTitle = styled(Box)`
    font-size: 16px;
    color: ${theme.color.white};
    margin-top: 40px;
    margin-bottom: 20px;
    font-weight: 600;
`;

export const HistoryContainer = styled(Box)`
    background-color: ${theme.color.blackRock};
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    position: relative;
    padding: 8px 0;
`;

export const ActivityRow = styled(Box)`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 10px 0;
    position: relative;
`;

export const ActivityColumn = styled(Box)`
    text-align: left;
    margin-left: 20px;
    margin-right: 5px;
    align-items: center;
    flex-direction: row;
`;

export const IconColumn = styled(Box)`
    position: relative;
    display: flex;
    width: 12px;
    height: inherit;
    align-items: flex-start;
    & > svg {
        z-index: 2;
    }
`;

export const IconBox = styled(Box)`
    flex: 1;
    flex-direction: column;
    align-items: center;
`;

export const VerticalLine = styled(Box)`
    width: 1px;
    background-color: ${theme.color.white10};
    height: 100%;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
`;

export const DateContainer = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 150px;
`;

export const DatePart = styled(Box)`
    font-size: 12px;
    color: ${theme.color.white50};
    margin-right: 10px;
`;

export const TimePart = styled(Box)`
    font-size: 12px;
    color: ${theme.color.white30};
`;

export const ActivityHeader = styled(Box)`
    color: ${theme.color.white};
    font-size: 12px;
    span {
        font-size: 12px;
    }
`;

export const EmptyState = styled(Box)`
    color: ${theme.color.white50};
    font-size: 14px;
    padding: 20px;
    text-align: center;
`;

// Skeleton background matches MUI's grey.800 (#424242) used for dark-mode loading states
export const TextSkeleton = styled(Skeleton)`
    && {
        background-color: #424242;
    }
`;

export const CardSkeleton = styled(Skeleton)`
    && {
        background-color: #424242;
        border-radius: 4px;
        margin-top: 16px;
    }
`;

export const LlmInfoButton = styled(Button)`
    text-transform: none;
    color: ${theme.color.white};
    border-color: ${theme.color.white50};
`;

export const PrLinkList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const PrLink = styled('a')`
    font-size: 13px;
    color: ${theme.color.lightningYellow};
    text-decoration: none;
    word-break: break-all;
    &:hover {
        text-decoration: underline;
    }
`;
